// Polish resources. Keys mirror en.ts. Render currency UI term = "gemy" (rebrand of
// internal `tokeny`; code/DB unchanged). Polish has 3 plural forms — see gemWord().
// Public-storefront namespaces live in ./pl/<surface>.ts (one per page).
import { landing } from './pl/landing'
import { pricing } from './pl/pricing'
import { faq } from './pl/faq'
import { auth } from './pl/auth'
import { legal } from './pl/legal'
// Logged-in app pages (one namespace per route).
import { appDashboard } from './pl/appDashboard'
import { appSettings } from './pl/appSettings'
import { appGenerator } from './pl/appGenerator'
import { appBilling } from './pl/appBilling'
import { appMusic } from './pl/appMusic'
import { appAccounts } from './pl/appAccounts'
import { appCampaigns } from './pl/appCampaigns'
import { appCalendar } from './pl/appCalendar'
import { appNotifications } from './pl/appNotifications'
import { appStats } from './pl/appStats'
import { appClipPacks } from './pl/appClipPacks'
import { appBatches } from './pl/appBatches'
import { appHooks } from './pl/appHooks'
import { appCaptions } from './pl/appCaptions'
import { appEditDetail } from './pl/appEditDetail'
import { appLyrics } from './pl/appLyrics'
import { appPackDetail } from './pl/appPackDetail'
import { appPackQa } from './pl/appPackQa'
import { appCollections } from './pl/appCollections'
import { appStorage } from './pl/appStorage'
import { appLyricsAdv } from './pl/appLyricsAdv'
// Cross-cutting surfaces (not tied to one route).
import { dialogs } from './pl/dialogs'
import { appComposer } from './pl/appComposer'
import { tiktokPrivacy } from './pl/tiktokPrivacy'
import { checkout } from './pl/checkout'
import { errors } from './pl/errors'
import { onboarding } from './pl/onboarding'
import { series } from './pl/series'

export const pl = {
  common: {
    loading: 'Ładowanie…',
    save: 'Zapisz',
    cancel: 'Anuluj',
    close: 'Zamknij',
    retry: 'Spróbuj ponownie',
    // Wartość z serwera, której słownik nie zna (`tEnum`, paczka 12, G7-account-token2-34):
    // neutralne słowo zamiast surowego ciągu — nigdy `schedule` ani `appCampaigns.status.x`.
    unspecified: 'nieokreślone',
    // Ulubione (reguła 58): gwiazdka na rzeczy, pigułka filtru, pusty wynik filtru
    // i JEDYNY toast tej funkcji — sukces nie mówi nic, bo widać go na gwiazdce.
    favorite: 'Ulubione',
    favorites: 'Ulubione',
    favoritesEmpty: 'Brak ulubionych',
    showAll: 'Pokaż wszystkie',
    favoriteError: 'Nie udało się zapisać ulubionych.',
    // Pełnopanelowy stan błędu (`components/StateScreens.tsx`, siedem tras). Mieszka
    // w `common`, a NIE w `errors`: tamten namespace mapuje KODY błędu z API na zdania
    // i jego klucze muszą być dosłownymi kodami serwera. Stan ekranu nie jest kodem.
    // Przycisk to `common.retry` — istniał wcześniej i drugiej kopii nie robimy.
    error: {
      title: 'Nie udało się wczytać danych',
      // Dwie rzeczy, które klient chce wiedzieć natychmiast: czyja to wina i czy stracił
      // pieniądze. „Coś po naszej stronie nie zaskoczyło” mówiło pierwsze zbyt potocznie
      // i nie dało się tego przetłumaczyć na angielski bez zgadywania.
      body: 'To problem po naszej stronie, nie po Twojej. Twoje edity i gemy są bezpieczne - odśwież za chwilę.',
      // Wyjście z ekranu, który się wywrócil. Po MINIMAL-2026-09-18 ekranem startowym są
      // Edity, więc przycisk nazywa miejsce tak, jak nazywa je nawigacja — przycisk
      // odsylajacy do miejsca o innej nazwie to druga zagadka dla kogoś, kto ma już jedną.
      backToDashboard: 'Wróć do editów',
    },
  },
  // Ekran 404. Do 2026-08-30 był po polsku na sztywno, w pliku bez ani jednego `t()`,
  // więc anglojęzyczny klient dostawał polskie zdanie — a przy literówce w adresie w
  // ogóle go nie widział, bo trasa `/:locale` przekierowywała na polską stronę główną
  // (WALK-DEFECTS D2).
  notFound: {
    title: 'Nie ma tu żadnego edita',
    body: 'Ta strona zniknęła, zmieniła adres albo nigdy nie istniała.',
    // Dwa różne wyjścia, bo to dwie różne osoby. Zalogowany wraca do swojej pracy;
    // ktoś z zewnątrz nie ma panelu — wysłanie go na `/edits` kończy się ścianą
    // logowania, czyli drugą zagadką dla kogoś, kto ma już jedną.
    backToDashboard: 'Wróć do editów',
    library: 'Biblioteka',
    backToSite: 'Wróć na stronę główną',
  },
  lang: { label: 'Język', en: 'English', pl: 'Polski', switch: 'Zmień język', switchTo: 'Zmień język na {name}' },
  nav: {
    generator: 'Generator',
    // Pozycja sidebara istniała w Layout.tsx od merge'u P03 BEZ tego klucza
    // — i była literałem w tablicy, więc skan `t()` jej nie widział.
    // Na prodzie w nawigacji stało dosłowne „nav.batches".
    batches: 'Edity',
    // MINIMAL-2026-09-18 (MAPA 30, decyzja 4): przy „Edity" stoi NEUTRALNY licznik
    // renderów w toku (liczba jest rysunkiem, więc zdanie niesie czytnikowi ten klucz),
    // a sygnał „coś wymaga ręki" to OSOBNA kropka — nigdy kolor licznika.
    rendersInFlight: 'rendery w toku: {count}',
    needsAttention: 'wymaga uwagi',
    library: 'Wszystkie edity',
    sectionLibrary: 'Biblioteka',
    music: 'Muzyka',
    clipPacks: 'Klipy',
    hooks: 'Hooki',
    // Trzecia instancja tego samego mechanizmu co `batches` wyzej: pozycja
    // sidebara jako literal w tablicy konfiguracyjnej (appNav.ts), wiec skan
    // `t()` jej nie widzi. Zlapala ja dopiero bramka i18n-keys-complete (P04),
    // ktora skanuje literaly key-ksztaltne wszedzie, nie tylko w `t(`.
    captions: 'Opisy',
    // Paczka L (reguła 43): etykiety grup w pasie zakładek Biblioteki. Wersaliki rysuje
    // CSS (`.mn-tabs-glabel`), więc czytnik dostaje słowo, a nie literowanie.
    groupGenerator: 'Generator',
    groupPublishing: 'Publikacja',
    // Publikacja (sesja 12 + 22, MINIMAL-2026-09-18): POZYCJA nawigacji dla konta
    // z dostępem (`GET /api/campaigns` → `enabled`), a jej zakładki to Planer ·
    // Kalendarz · Twoje konta · Opisy (wsad 22 §1).
    sectionDistribution: 'Publikacja',
    // Słownik planu mówi „Planer", nie „Autopilot" i nie „Planer publikacji": ta sama
    // nazwa stoi w zakładce i w tytule ekranu (plan, zasada 7).
    campaigns: 'Planer',
    calendar: 'Kalendarz',
    // Sesja 35: zakładka nazywa się „Konta”. Tytułem ekranu jest „Publikacja”, więc
    // „Twoje” nie ma już czego odróżniać, a krótsza etykieta mieści pięć zakładek na 390.
    accounts: 'Konta',
    // Piąta zakładka Publikacji (sesja 36); pokazuje się tylko wtedy, gdy funkcja istnieje.
    stats: 'Statystyki',
    sectionAccount: 'Konto',
    billing: 'Tokeny i plan',
    settings: 'Ustawienia',
    // Nazwa przycisku menu 390 dla czytnika (reguła 29). Klucz `more` UMARŁ razem
    // z dolnym paskiem i arkuszem „Więcej” (R5): szuflada niesie cały panel.
    menu: 'Menu',
    // Saldo w powłoce (R3, zasada 8 + reguła 34): widoczna jest liczba i „Doładuj”.
    // ZDAŃ pod saldem nie ma — stan niesie kolor liczby, a wstrzymaną sprzedaż
    // nieaktywne „Doładuj”. Dla czytnika zostaje sama NAZWA stanu, bo kolor to
    // jedyny widoczny nośnik i bez niej stan po prostu nie istnieje.
    topup: 'Doładuj',
    gemsLow: 'niski stan',
    gemsZero: 'brak tokenów',
    gemsNoPay: 'sprzedaż wstrzymana',
    loadingRoute: 'Wczytujemy…',
  },
  topbar: { gems: 'Gemy', tokens: 'Tokeny', topup: 'Doładuj gemy', planAndBalance: '{plan} · gemy: {balance}', renewal: 'odnowienie {date}' },
  userMenu: { language: 'Język', logout: 'Wyloguj się' },
  pending: {
    rejectedTitle: 'Zgłoszenie nie zostało zaakceptowane',
    rejectedBody: 'Twoje konto nie zostało aktywowane. Jeśli to pomyłka, napisz do nas.',
    logout: 'Wyloguj się',
  },
  dashboard: {
    topup: 'Doładuj gemy',
    low: 'niski stan',
    available: 'dostępne',
  },
  settings: {
    languageRegion: 'Język i region',
    language: 'Język',
  },
  support: {
    button: 'Pomoc',
    title: 'Pomoc',
    subtitle: 'Zwykle odpowiadamy w ciągu doby.',
    intro: 'Masz pytanie? Napisz do nas - chętnie pomożemy.',
    placeholder: 'Napisz wiadomość',
    send: 'Wyślij',
    you: 'Ty',
    team: 'EDIMI',
    sending: 'Wysyłanie',
    // Błąd wysyłki stoi w osobnym wierszu nad przyciskiem, z kodem (09d).
    sendFailed: 'Nie udało się wysłać wiadomości. Spróbuj ponownie.',
    close: 'Zamknij',
  },
  cookie: {
    aria: 'Informacja o plikach cookie',
    title: 'Pliki cookie',
    // Jedna informacja zgodna z §4 Polityki (`legal.polityka.p4`): nie prosimy o zgodę,
    // bo nie używamy cookie, które by jej wymagały. Wybór wraca razem z analityką.
    body: 'Używamy tylko plików cookie niezbędnych do logowania i płatności - bez zgody, bo żadne jej nie wymaga. Szczegóły w',
    policy: 'Polityce prywatności',
    // Paczka 37 (artboard 01): jedna linia, jedno zdanie, link jako nazwa dokumentu.
    // Stare klucze zostają — wracają z analityką i dwoma przyciskami.
    bodyMinimal: 'Używamy tylko plików cookie potrzebnych do logowania i płatności. Bez zgody, bo żadna nie jest wymagana.',
    policyMinimal: 'Polityka prywatności',
    dismiss: 'Rozumiem',
  },
  landing,
  pricing,
  faq,
  auth,
  legal,
  appDashboard,
  appSettings,
  appGenerator,
  appBilling,
  appMusic,
  appAccounts,
  appCampaigns,
  appCalendar,
  appNotifications,
  appStats,
  appClipPacks,
  appBatches,
  appHooks,
  appCaptions,
  appEditDetail,
  appLyrics,
  appPackDetail,
  appPackQa,
  appCollections,
  appStorage,
  appLyricsAdv,
  dialogs,
  appComposer,
  tiktokPrivacy,
  checkout,
  errors,
  onboarding,
  series,
}
